import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Chip, Avatar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { usersAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';

const UserForm = ({ open, onClose, user, roles, onSaved }) => {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    reset(user ? { ...user, role_ids: user.role_ids || [] } : { is_active: true, role_ids: [] });
  }, [user, open, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (user?.id) { await usersAPI.update(user.id, data); toast.success('User updated'); }
      else { await usersAPI.create(data); toast.success('User created'); }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save user');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{user?.id ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="First Name *" {...register('first_name', { required: true })} error={!!errors.first_name} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Last Name *" {...register('last_name', { required: true })} error={!!errors.last_name} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Email *" type="email" {...register('email', { required: true })} error={!!errors.email} /></Grid>
            {!user?.id && <Grid item xs={12}><TextField fullWidth label="Password *" type="password" {...register('password', { required: !user?.id, minLength: { value: 8, message: 'Min 8 chars' } })} error={!!errors.password} helperText={errors.password?.message} /></Grid>}
            <Grid item xs={12}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
            <Grid item xs={12}>
              <Controller name="role_ids" control={control} defaultValue={[]} render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Roles</InputLabel>
                  <Select multiple {...field} input={<OutlinedInput label="Roles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(id => <Chip key={id} label={roles.find(r => r.id === id)?.name || id} size="small" />)}
                      </Box>
                    )}>
                    {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )} />
            </Grid>
            {user?.id && (
              <Grid item xs={12}>
                <Controller name="is_active" control={control} defaultValue={true} render={({ field }) => (
                  <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                    <Select {...field} label="Status"><MenuItem value={true}>Active</MenuItem><MenuItem value={false}>Inactive</MenuItem></Select>
                  </FormControl>
                )} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : (user?.id ? 'Update' : 'Create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]); const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(''); const [formOpen, setFormOpen] = useState(false); const [editUser, setEditUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try { setLoading(true);
      const { data } = await usersAPI.getAll({ page: page+1, limit: rowsPerPage, search: search||undefined });
      setUsers(data.data||[]); setTotal(data.pagination?.total||0);
    } catch { toast.error('Failed to load users'); } finally { setLoading(false); }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchUsers();
    usersAPI.getRoles().then(r => setRoles(r.data.data||[]));
  }, [fetchUsers]);

  const columns = [
    { key: 'first_name', label: 'User', render: (_, row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}>{row.first_name?.[0]}{row.last_name?.[0]}</Avatar>
        <Box><Typography variant="body2" fontWeight={600}>{row.first_name} {row.last_name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography></Box>
      </Box>
    )},
    { key: 'roles', label: 'Roles', render: (v) => <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {(v||[]).filter(Boolean).map(r => <Chip key={r} label={r} size="small" color="primary" variant="outlined" />)}
    </Box>},
    { key: 'is_active', label: 'Status', render: (v) => <StatusChip status={v ? 'active' : 'inactive'} /> },
    { key: 'last_login_at', label: 'Last Login', render: (v) => v ? new Date(v).toLocaleDateString() : 'Never' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button size="small" onClick={() => { setEditUser(row); setFormOpen(true); }}>Edit</Button>
        <Button size="small" color="error" onClick={async () => { if(window.confirm('Delete user?')) { await usersAPI.delete(v); toast.success('Deleted'); fetchUsers(); }}}>Delete</Button>
      </Box>
    )},
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>User Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditUser(null); setFormOpen(true); }}>Add User</Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField placeholder="Search users..." size="small" sx={{ minWidth: 280 }}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>
      <DataTable columns={columns} rows={users} loading={loading} total={total} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
      <UserForm open={formOpen} onClose={() => setFormOpen(false)} user={editUser} roles={roles} onSaved={fetchUsers} />
    </Box>
  );
};

export default UsersPage;
